import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { supabase } from '../../lib/supabase';

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const { adminUser, loading: authLoading, isAuthenticated, logout } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('courts');
  const [showAddCourtModal, setShowAddCourtModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [editingCourt, setEditingCourt] = useState<any>(null);
  const [selectedCourtForPhoto, setSelectedCourtForPhoto] = useState<any>(null);
  const [selectedCourt, setSelectedCourt] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [courts, setCourts] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [resources, setResources] = useState<any[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [reports, setReports] = useState<any[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [newCourt, setNewCourt] = useState({
    name: '',
    location: '',
    district: '',
    address: '',
    contact_phone: '',
    contact_email: '',
    description: '',
    hourly_rate: '',
    amenities: [] as string[],
    operating_hours: {
      monday: { open: '06:00', close: '22:00', closed: false },
      tuesday: { open: '06:00', close: '22:00', closed: false },
      wednesday: { open: '06:00', close: '22:00', closed: false },
      thursday: { open: '06:00', close: '22:00', closed: false },
      friday: { open: '06:00', close: '22:00', closed: false },
      saturday: { open: '06:00', close: '22:00', closed: false },
      sunday: { open: '06:00', close: '22:00', closed: false }
    }
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [resourceLanguageFilter, setResourceLanguageFilter] = useState('all');
  
  // Add statistics state
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCourts: 0,
    totalSessions: 0,
    totalBookings: 0,
    usersGrowth: 0,
    sessionsGrowth: 0,
    bookingsGrowth: 0,
    revenueGrowth: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchCourts = async () => {
    try {
      const { data, error } = await supabase
        .from('courts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCourts(data || []);
    } catch (error) {
      console.error('Error fetching courts:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      setMessagesLoading(true);
      const { data, error } = await supabase
        .from('direct_messages')
        .select(`
          *,
          sender:sender_id(id, email, nickname, avatar_url),
          receiver:receiver_id(id, email, nickname, avatar_url)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      // Fallback: fetch messages without joins and then fetch user data separately
      try {
        const { data: messagesData, error: messagesError } = await supabase
          .from('direct_messages')
          .select('*')
          .order('created_at', { ascending: false });

        if (messagesError) throw messagesError;

        // Get unique user IDs
        const userIds = [...new Set([
          ...messagesData.map(msg => msg.sender_id),
          ...messagesData.map(msg => msg.receiver_id)
        ].filter(Boolean))];

        // Fetch user profiles
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, email, nickname, avatar_url')
          .in('id', userIds);

        if (profilesError) throw profilesError;

        // Create a map of user profiles
        const profilesMap = profilesData.reduce((acc, profile) => {
          acc[profile.id] = profile;
          return acc;
        }, {});

        // Combine messages with user data
        const messagesWithProfiles = messagesData.map(message => ({
          ...message,
          sender: profilesMap[message.sender_id] || null,
          receiver: profilesMap[message.receiver_id] || null
        }));

        setMessages(messagesWithProfiles);
      } catch (fallbackError) {
        console.error('Fallback error:', fallbackError);
        setMessages([]);
      }
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleViewMessages = () => {
    setShowMessagesModal(true);
    fetchMessages();
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!confirm('Are you sure you want to delete this message?')) return;

    try {
      const { error } = await supabase
        .from('direct_messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;

      // Remove from local state
      setMessages(messages.filter(msg => msg.id !== messageId));
      alert('Message deleted successfully!');
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  // Add function to fetch real statistics
  const fetchStatistics = async () => {
    try {
      setStatsLoading(true);

      // Fetch total users
      const { count: usersCount, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      if (usersError) throw usersError;

      // Fetch total courts
      const { count: courtsCount, error: courtsError } = await supabase
        .from('courts')
        .select('*', { count: 'exact', head: true });

      if (courtsError) throw courtsError;

      // Fetch total sessions
      const { count: sessionsCount, error: sessionsError } = await supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true });

      if (sessionsError) throw sessionsError;

      // Fetch total bookings
      const { count: bookingsCount, error: bookingsError } = await supabase
        .from('session_bookings')
        .select('*', { count: 'exact', head: true });

      if (bookingsError) throw bookingsError;

      // Calculate growth rates (comparing last 30 days vs previous 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

      // Users growth
      const { count: recentUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      const { count: previousUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', sixtyDaysAgo.toISOString())
        .lt('created_at', thirtyDaysAgo.toISOString());

      const usersGrowth = previousUsers > 0 
        ? Math.round(((recentUsers - previousUsers) / previousUsers) * 100)
        : recentUsers > 0 ? 100 : 0;

      // Sessions growth
      const { count: recentSessions } = await supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      const { count: previousSessions } = await supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', sixtyDaysAgo.toISOString())
        .lt('created_at', thirtyDaysAgo.toISOString());

      const sessionsGrowth = previousSessions > 0 
        ? Math.round(((recentSessions - previousSessions) / previousSessions) * 100)
        : recentSessions > 0 ? 100 : 0;

      // Bookings growth
      const { count: recentBookings } = await supabase
        .from('session_bookings')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      const { count: previousBookings } = await supabase
        .from('session_bookings')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', sixtyDaysAgo.toISOString())
        .lt('created_at', thirtyDaysAgo.toISOString());

      const bookingsGrowth = previousBookings > 0 
        ? Math.round(((recentBookings - previousBookings) / previousBookings) * 100)
        : recentBookings > 0 ? 100 : 0;

      setStats({
        totalUsers: usersCount || 0,
        totalCourts: courtsCount || 0,
        totalSessions: sessionsCount || 0,
        totalBookings: bookingsCount || 0,
        usersGrowth,
        sessionsGrowth,
        bookingsGrowth,
        revenueGrowth: bookingsGrowth // Use bookings growth as revenue growth estimate
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchCourts();
      if (activeTab === 'users') {
        fetchUsers();
      }
      if (activeTab === 'stats') {
        fetchStatistics();
      }
    }
  }, [isAuthenticated, activeTab]);

  const loadCourts = async () => {
    await fetchCourts();
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Don't render admin content if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const handleAddCourt = async () => {
    try {
      const { data, error } = await supabase
        .from('courts')
        .insert({
          name: newCourt.name,
          location: newCourt.location,
          district: newCourt.district,
          address: newCourt.address,
          contact_phone: newCourt.contact_phone || null,
          contact_email: newCourt.contact_email || null,
          description: newCourt.description || null,
          hourly_rate: newCourt.hourly_rate ? parseFloat(newCourt.hourly_rate) : null,
          amenities: newCourt.amenities.length > 0 ? newCourt.amenities : null,
          operating_hours: newCourt.operating_hours
        })
        .select()

      if (error) throw error

      // Refresh courts list
      loadCourts()
      
      // Reset form and close modal
      setNewCourt({
        name: '',
        location: '',
        district: '',
        address: '',
        contact_phone: '',
        contact_email: '',
        description: '',
        hourly_rate: '',
        amenities: [],
        operating_hours: {
          monday: { open: '06:00', close: '22:00', closed: false },
          tuesday: { open: '06:00', close: '22:00', closed: false },
          wednesday: { open: '06:00', close: '22:00', closed: false },
          thursday: { open: '06:00', close: '22:00', closed: false },
          friday: { open: '06:00', close: '22:00', closed: false },
          saturday: { open: '06:00', close: '22:00', closed: false },
          sunday: { open: '06:00', close: '22:00', closed: false }
        }
      })
      setShowAddCourtModal(false)
      
      alert('Court added successfully!')
    } catch (error) {
      console.error('Error adding court:', error)
      alert('Failed to add court. Please try again.')
    }
  }

  const handleAmenityToggle = (amenity: string) => {
    setNewCourt(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }))
  }

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedCourts = [...courts].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    if (sortField === 'hourly_rate') {
      aValue = aValue || 0;
      bValue = bValue || 0;
    }
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleEditCourt = (court: any) => {
    setEditingCourt({
      ...court,
      amenities: court.amenities || [],
      operating_hours: court.operating_hours || {
        monday: { open: '06:00', close: '22:00', closed: false },
        tuesday: { open: '06:00', close: '22:00', closed: false },
        wednesday: { open: '06:00', close: '22:00', closed: false },
        thursday: { open: '06:00', close: '22:00', closed: false },
        friday: { open: '06:00', close: '22:00', closed: false },
        saturday: { open: '06:00', close: '22:00', closed: false },
        sunday: { open: '06:00', close: '22:00', closed: false }
      }
    });
    setShowEditModal(true);
  };

  const handleUpdateCourt = async () => {
    if (!editingCourt) return;

    try {
      setLoading(true);
      setErrorMessage('');
      
      // Log the data being sent for debugging
      console.log('Updating court with data:', {
        id: editingCourt.id,
        name: editingCourt.name,
        location: editingCourt.location,
        district: editingCourt.district,
        address: editingCourt.address,
        contact_phone: editingCourt.contact_phone,
        contact_email: editingCourt.contact_email
      });

      const { data, error } = await supabase
        .from('courts')
        .update({
          name: editingCourt.name,
          location: editingCourt.location,
          district: editingCourt.district,
          address: editingCourt.address,
          contact_phone: editingCourt.contact_phone || null,
          contact_email: editingCourt.contact_email || null,
          description: editingCourt.description || null,
          hourly_rate: editingCourt.hourly_rate ? parseFloat(editingCourt.hourly_rate) : null,
          amenities: editingCourt.amenities.length > 0 ? editingCourt.amenities : null,
          operating_hours: editingCourt.operating_hours
        })
        .eq('id', editingCourt.id)
        .select();

      if (error) {
        console.error('Update error:', error);
        throw error;
      }

      console.log('Update successful:', data);

      // Update local state immediately with the edited data
      setCourts(prevCourts => 
        prevCourts.map(court => 
          court.id === editingCourt.id ? { ...court, ...editingCourt } : court
        )
      );
      
      // Also refresh from database to ensure sync
      await fetchCourts();
      
      setShowEditModal(false);
      setEditingCourt(null);
      setSuccessMessage('Court updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating court:', error);
      setErrorMessage(`Failed to update court: ${error.message || 'Please try again.'}`);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAmenityToggle = (amenity: string) => {
    setEditingCourt(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleReplaceWithRealCourts = async () => {
    if (!confirm('This will replace all existing courts with real Hong Kong pickleball courts. Are you sure?')) {
      return;
    }

    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/bulk-add-courts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ action: 'clear_and_add_real_courts' })
      });

      const result = await response.json();

      if (result.success) {
        alert(`Success! Added ${result.courts_added} real Hong Kong pickleball courts`);
        fetchCourts(); // Refresh the courts list
      } else {
        throw new Error(result.error || 'Failed to add courts');
      }
    } catch (error) {
      console.error('Error replacing courts:', error);
      alert('Failed to replace courts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (courtId: string, file: File) => {
    try {
      setUploadingPhoto(courtId);
      
      // Validate file
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Please upload a JPG, PNG, or WebP image file');
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        throw new Error('File size must be less than 5MB');
      }

      // Get current court data to delete existing photo
      const { data: court, error: courtError } = await supabase
        .from('courts')
        .select('photo_url')
        .eq('id', courtId)
        .single();

      if (courtError) {
        throw new Error('Court not found');
      }

      // Delete existing photo from storage if exists
      if (court.photo_url) {
        const existingFileName = court.photo_url.split('/').pop();
        if (existingFileName) {
          await supabase.storage
            .from('court-photos')
            .remove([existingFileName]);
        }
      }

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `court-${courtId}-${Date.now()}.${fileExt}`;
      
      // Upload to Supabase Storage using authenticated client
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('court-photos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error(`Failed to upload photo: ${uploadError.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('court-photos')
        .getPublicUrl(fileName);

      // Update court record with photo URL
      const { error: updateError } = await supabase
        .from('courts')
        .update({ photo_url: publicUrl })
        .eq('id', courtId);

      if (updateError) {
        console.error('Update error:', updateError);
        // Clean up uploaded file if database update fails
        await supabase.storage
          .from('court-photos')
          .remove([fileName]);
        throw new Error(`Failed to update court record: ${updateError.message}`);
      }

      // Refresh courts data
      await fetchCourts();
      
      setSuccessMessage('Photo uploaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowPhotoModal(false);
      setSelectedCourtForPhoto(null);
      
    } catch (error) {
      console.error('Photo upload error:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to upload photo');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handlePhotoDelete = async (courtId: string) => {
    try {
      setUploadingPhoto(courtId);
      
      // Find the court to get the photo URL
      const court = courts.find(c => c.id === courtId);
      if (!court?.photo_url) {
        throw new Error('No photo to delete');
      }

      // Extract filename from URL
      const urlParts = court.photo_url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      
      // Delete from storage
      const { error: deleteError } = await supabase.storage
        .from('court-photos')
        .remove([fileName]);

      if (deleteError) {
        console.error('Delete error:', deleteError);
        throw new Error(`Failed to delete photo: ${deleteError.message}`);
      }

      // Update court record to remove photo URL
      const { error: updateError } = await supabase
        .from('courts')
        .update({ photo_url: null })
        .eq('id', courtId);

      if (updateError) {
        console.error('Update error:', updateError);
        throw new Error(`Failed to update court record: ${updateError.message}`);
      }

      // Refresh courts data
      await fetchCourts();
      
      setSuccessMessage('Photo deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Photo delete error:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to delete photo');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleDeleteCourt = (court: any) => {
    setSelectedCourt(court);
    setShowDeleteModal(true);
  };

  const confirmDeleteCourt = async () => {
    if (!selectedCourt) return;

    try {
      setDeleteLoading(true);
      
      // Delete the court from database
      const { error } = await supabase
        .from('courts')
        .delete()
        .eq('id', selectedCourt.id);

      if (error) throw error;

      // Remove from local state
      setCourts(courts.filter(court => court.id !== selectedCourt.id));
      
      setShowDeleteModal(false);
      setSelectedCourt(null);
      alert('Court deleted successfully!');
    } catch (error) {
      console.error('Error deleting court:', error);
      alert('Failed to delete court. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const tabs = [
    { id: 'courts', label: 'Courts', icon: 'ri-map-pin-line' },
    { id: 'photos', label: 'Photos', icon: 'ri-image-line' },
    { id: 'sessions', label: 'Sessions', icon: 'ri-calendar-line' },
    { id: 'users', label: 'Users', icon: 'ri-user-line' },
    { id: 'content', label: 'Content', icon: 'ri-file-text-line' },
    { id: 'stats', label: 'Statistics', icon: 'ri-bar-chart-line' }
  ];

  const mockCourts = [
    { id: 1, name: 'Central Park Courts', location: 'Central District', status: 'Active', bookings: 45 },
    { id: 2, name: 'Waterfront Sports Complex', location: 'Harbor District', status: 'Active', bookings: 32 },
    { id: 3, name: 'Community Center Courts', location: 'Residential Area', status: 'Maintenance', bookings: 0 }
  ];

  const mockSessions = [
    { id: 1, title: 'Morning Practice', court: 'Central Park Courts', date: '2024-01-15', time: '09:00', participants: 8, status: 'Active' },
    { id: 2, title: 'Evening Tournament', court: 'Waterfront Sports Complex', date: '2024-01-16', time: '18:00', participants: 16, status: 'Pending' },
    { id: 3, title: 'Beginner Training', court: 'Community Center Courts', date: '2024-01-17', time: '14:00', participants: 6, status: 'Active' }
  ];

  const mockUsers = [
    { id: 1, name: 'John Smith', email: 'john@example.com', role: 'Member', joinDate: '2023-12-01', status: 'Active' },
    { id: 2, name: 'Sarah Johnson', email: 'sarah@example.com', role: 'Member', joinDate: '2023-11-15', status: 'Active' },
    { id: 3, name: 'Mike Chen', email: 'mike@example.com', role: 'Admin', joinDate: '2023-10-20', status: 'Active' }
  ];

  const renderCourtsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Court Management</h2>
        <div className="flex space-x-3">
          <button 
            onClick={() => setShowAddCourtModal(true)}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>Add New Court
          </button>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                onClick={() => handleSort('name')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center">
                  Court Name
                  <i className={`ml-1 ${sortField === 'name' ? (sortDirection === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line') : 'ri-arrow-up-down-line'}`}></i>
                </div>
              </th>
              <th 
                onClick={() => handleSort('location')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center">
                  Location
                  <i className={`ml-1 ${sortField === 'location' ? (sortDirection === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line') : 'ri-arrow-up-down-line'}`}></i>
                </div>
              </th>
              <th 
                onClick={() => handleSort('district')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center">
                  District
                  <i className={`ml-1 ${sortField === 'district' ? (sortDirection === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line') : 'ri-arrow-up-down-line'}`}></i>
                </div>
              </th>
              <th 
                onClick={() => handleSort('hourly_rate')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center">
                  Rate (HKD/hr)
                  <i className={`ml-1 ${sortField === 'hourly_rate' ? (sortDirection === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line') : 'ri-arrow-up-down-line'}`}></i>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedCourts.length > 0 ? sortedCourts.map((court) => (
              <tr key={court.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{court.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{court.location}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{court.district}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {court.hourly_rate ? `$${court.hourly_rate}` : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button 
                    onClick={() => handleEditCourt(court)}
                    className="text-teal-600 hover:text-teal-900 cursor-pointer mr-4"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => {
                      setSelectedCourtForPhoto(court);
                      setShowPhotoModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-900 cursor-pointer mr-4"
                  >
                    Photo
                  </button>
                  <button 
                    onClick={() => handleDeleteCourt(court)}
                    className="text-red-600 hover:text-red-900 cursor-pointer"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            )) : mockCourts.map((court) => (
              <tr key={court.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{court.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{court.location}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    court.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {court.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{court.bookings}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button 
                    onClick={() => handleEditCourt(court)}
                    className="text-teal-600 hover:text-teal-900 cursor-pointer mr-4"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteCourt(court)}
                    className="text-red-600 hover:text-red-900 cursor-pointer"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPhotosTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Photo Management</h2>
        <div className="text-sm text-gray-600">
          Upload real photos for each court to replace stock images
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courts.map((court) => (
          <div key={court.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="aspect-w-16 aspect-h-9 bg-gray-200">
              {court.photo_url ? (
                <img
                  src={court.photo_url}
                  alt={court.name}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-image-line text-4xl text-gray-400 mb-2"></i>
                    <p className="text-gray-500 text-sm">No photo uploaded</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-2">{court.name}</h3>
              <p className="text-sm text-gray-600">{court.location}, {court.district}</p>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedCourtForPhoto(court);
                    setShowPhotoModal(true);
                  }}
                  className="flex-1 bg-teal-600 text-white px-3 py-2 rounded-lg hover:bg-teal-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-upload-line mr-1"></i>
                  {court.photo_url ? 'Replace' : 'Upload'}
                </button>
                
                {court.photo_url && (
                  <button
                    onClick={() => handlePhotoDelete(court.id)}
                    className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {courts.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-image-line text-6xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No courts found</h3>
          <p className="text-gray-600">Add some courts first to manage their photos.</p>
        </div>
      )}
    </div>
  );

  const renderSessionsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Session Management</h2>
        <button className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-add-line mr-2"></i>Create Session
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Court</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Participants</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {mockSessions.map((session) => (
              <tr key={session.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{session.title}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{session.court}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{session.date} at {session.time}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{session.participants}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    session.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {session.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button className="text-teal-600 hover:text-teal-900 cursor-pointer">Edit</button>
                  <button className="text-red-600 hover:text-red-900 cursor-pointer">Cancel</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Add new handler functions for user management
  const handleViewUser = (user: any) => {
    setSelectedUser(user);
    setShowUserDetailModal(true);
  };

  const handleEditUser = (user: any) => {
    setSelectedUser(user);
    setShowEditUserModal(true);
  };

  const handleSuspendUser = async (userId: string) => {
    if (!confirm('Are you sure you want to suspend this user? They will not be able to log in.')) {
      return;
    }

    try {
      // In a real implementation, you would update the user's status in the database
      // For now, we'll show a success message
      alert('User suspended successfully! (Note: Full suspension functionality requires additional backend setup)');
      
      // Optionally refresh the users list
      fetchUsers();
    } catch (error) {
      console.error('Error suspending user:', error);
      alert('Failed to suspend user. Please try again.');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          nickname: selectedUser.nickname,
          bio: selectedUser.bio,
          skill_level: selectedUser.skill_level,
          location: selectedUser.location
        })
        .eq('id', selectedUser.id);

      if (error) throw error;

      // Update local state
      setUsers(users.map(user => 
        user.id === selectedUser.id ? selectedUser : user
      ));

      setShowEditUserModal(false);
      setSelectedUser(null);
      alert('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user. Please try again.');
    }
  };

  const renderUsersTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <div className="flex space-x-3">
          <button 
            onClick={fetchUsers}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line mr-2"></i>Refresh
          </button>
          <button className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-download-line mr-2"></i>Export Users
          </button>
        </div>
      </div>
      
      {usersLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i>
            <p className="text-gray-600">Loading users...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length > 0 ? users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.avatar_url ? (
                          <img className="h-10 w-10 rounded-full object-cover" src={user.avatar_url} alt={user.nickname || user.email} />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                            <i className="ri-user-line text-teal-600"></i>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.nickname || 'No nickname'}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {user.id.slice(0, 8)}...
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.is_admin ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.is_admin ? 'Admin' : 'Member'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      Active
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button 
                      onClick={() => handleViewUser(user)}
                      className="text-teal-600 hover:text-teal-900 cursor-pointer"
                    >
                      View
                    </button>
                    <button 
                      onClick={() => handleEditUser(user)}
                      className="text-blue-600 hover:text-blue-900 cursor-pointer"
                    >
                      Edit
                    </button>
                    {!user.is_admin && (
                      <button 
                        onClick={() => handleSuspendUser(user.id)}
                        className="text-red-600 hover:text-red-900 cursor-pointer"
                      >
                        Suspend
                      </button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="text-center">
                      <i className="ri-user-line text-6xl text-gray-300 mb-4"></i>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                      <p className="text-gray-600">No registered users in the system yet.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          
          {users.length > 0 && (
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{users.length}</span> users
                </div>
                <div className="text-sm text-gray-500">
                  Total: {users.filter(u => !u.is_admin).length} members, {users.filter(u => u.is_admin).length} admins
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const fetchResources = async () => {
    try {
      setResourcesLoading(true);
      // Mock data for learning resources - both English and Chinese
      const mockResources = [
        // English Resources
        {
          id: 1,
          title: 'Pickleball Basics for Beginners',
          title_zh: '匹克球初學者基礎',
          description: 'Learn the fundamental rules and techniques of pickleball',
          description_zh: '學習匹克球的基本規則和技巧',
          skill_level: 'Beginner',
          view_count: 1247,
          is_featured: true,
          language: 'en',
          thumbnail_url: 'https://public.readdy.ai/ai/img_res/161fa8fa85c0fe1fd11cd3b9ef4825c5.jpg',
          created_at: '2024-01-15T10:00:00Z'
        },
        {
          id: 2,
          title: 'Advanced Serving Techniques',
          title_zh: '進階發球技巧',
          description: 'Master different serving styles and strategies',
          description_zh: '掌握不同的發球風格和策略',
          skill_level: 'Advanced',
          view_count: 892,
          is_featured: false,
          language: 'en',
          thumbnail_url: 'https://readdy.ai/api/search-image?query=pickleball%20advanced%20serving%20technique%20professional%20player%20court%20action%20sports%20background&width=400&height=300&seq=2&orientation=landscape',
          created_at: '2024-01-14T15:30:00Z'
        },
        {
          id: 3,
          title: 'Court Positioning and Strategy',
          title_zh: '球場定位與策略',
          description: 'Learn optimal court positioning for doubles play',
          description_zh: '學習雙打比賽的最佳球場定位',
          skill_level: 'Intermediate',
          view_count: 654,
          is_featured: true,
          language: 'en',
          thumbnail_url: 'https://readdy.ai/api/search-image?query=pickleball%20court%20positioning%20strategy%20doubles%20play%20tactical%20diagram%20sports%20background&width=400&height=300&seq=3&orientation=landscape',
          created_at: '2024-01-13T09:15:00Z'
        },
        // Chinese Resources
        {
          id: 4,
          title: '匹克球基礎教學',
          title_zh: '匹克球基礎教學',
          description: '完整的匹克球入門指南，包含規則、技巧和基本戰術',
          description_zh: '完整的匹克球入門指南，包含規則、技巧和基本戰術',
          skill_level: 'Beginner',
          view_count: 2156,
          is_featured: true,
          language: 'zh',
          thumbnail_url: 'https://readdy.ai/api/search-image?query=pickleball%20chinese%20tutorial%20guide%20court%20rules%20explanation%20asian%20instructor%20teaching%20background&width=400&height=300&seq=4&orientation=landscape',
          created_at: '2024-01-12T14:20:00Z'
        },
        {
          id: 5,
          title: '雙打戰術與配合',
          title_zh: '雙打戰術與配合',
          description: '學習雙打比賽中的團隊配合、溝通技巧和戰術運用',
          description_zh: '學習雙打比賽中的團隊配合、溝通技巧和戰術運用',
          skill_level: 'Intermediate',
          view_count: 1743,
          is_featured: false,
          language: 'zh',
          thumbnail_url: 'https://readdy.ai/api/search-image?query=pickleball%20doubles%20strategy%20chinese%20players%20teamwork%20court%20positioning%20tactical%20play%20background&width=400&height=300&seq=5&orientation=landscape',
          created_at: '2024-01-11T11:45:00Z'
        },
        {
          id: 6,
          title: '進階技巧訓練',
          title_zh: '進階技巧訓練',
          description: '提升球技的進階訓練方法，包含各種擊球技巧和戰術應用',
          description_zh: '提升球技的進階訓練方法，包含各種擊球技巧和戰術應用',
          skill_level: 'Advanced',
          view_count: 967,
          is_featured: true,
          language: 'zh',
          thumbnail_url: 'https://readdy.ai/api/search-image?query=advanced%20pickleball%20training%20techniques%20chinese%20coach%20demonstration%20professional%20skills%20development%20background&width=400&height=300&seq=6&orientation=landscape',
          created_at: '2024-01-10T16:00:00Z'
        },
        {
          id: 7,
          title: '比賽規則詳解',
          title_zh: '比賽規則詳解',
          description: '詳細解說匹克球的官方比賽規則、計分方式和裁判標準',
          description_zh: '詳細解說匹克球的官方比賽規則、計分方式和裁判標準',
          skill_level: 'All Levels',
          view_count: 1567,
          is_featured: false,
          language: 'zh',
          thumbnail_url: 'https://readdy.ai/api/search-image?query=pickleball%20official%20rules%20chinese%20explanation%20referee%20guidelines%20scoring%20system%20educational%20background&width=400&height=300&seq=7&orientation=landscape',
          created_at: '2024-01-09T13:30:00Z'
        },
        {
          id: 8,
          title: '設備選擇指南',
          title_zh: '設備選擇指南',
          description: '如何選擇適合的球拍、球鞋和其他匹克球裝備',
          description_zh: '如何選擇適合的球拍、球鞋和其他匹克球裝備',
          skill_level: 'Beginner',
          view_count: 1234,
          is_featured: false,
          language: 'zh',
          thumbnail_url: 'https://readdy.ai/api/search-image?query=pickleball%20equipment%20guide%20chinese%20paddle%20selection%20shoes%20gear%20shopping%20guide%20background&width=400&height=300&seq=8&orientation=landscape',
          created_at: '2024-01-08T10:15:00Z'
        }
      ];
      setResources(mockResources);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    } finally {
      setResourcesLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      setReportsLoading(true);
      // Mock data for reports
      const mockReports = [
        {
          id: 1,
          reporter_name: 'John Smith',
          reporter_email: 'john@example.com',
          report_type: 'Inappropriate Content',
          reported_item: 'User Profile: Mike Chen',
          reason: 'Inappropriate profile picture and offensive bio content',
          status: 'Pending',
          created_at: '2024-01-15T14:30:00Z'
        },
        {
          id: 2,
          reporter_name: 'Sarah Johnson',
          reporter_email: 'sarah@example.com',
          report_type: 'Spam',
          reported_item: 'Session: "Free Coaching Session"',
          reason: 'User is posting fake coaching sessions to collect personal information',
          status: 'Pending',
          created_at: '2024-01-15T11:20:00Z'
        },
        {
          id: 3,
          reporter_name: 'David Wong',
          reporter_email: 'david@example.com',
          report_type: 'Fake Account',
          reported_item: 'User Profile: Tennis Pro 2024',
          reason: 'Using stolen photos and fake credentials',
          status: 'Resolved',
          created_at: '2024-01-14T16:45:00Z'
        }
      ];
      setReports(mockReports);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  };

  const handleManageResources = () => {
    setShowResourcesModal(true);
    fetchResources();
  };

  const handleReviewReports = () => {
    setShowReportsModal(true);
    fetchReports();
  };

  const handleToggleFeature = (resourceId: number) => {
    setResources(resources.map(resource => 
      resource.id === resourceId 
        ? { ...resource, is_featured: !resource.is_featured }
        : resource
    ));
  };

  const handleDeleteResource = (resourceId: number) => {
    if (confirm('Are you sure you want to delete this resource?')) {
      setResources(resources.filter(resource => resource.id !== resourceId));
      alert('Resource deleted successfully!');
    }
  };

  const handleResolveReport = (reportId: number) => {
    setReports(reports.map(report => 
      report.id === reportId 
        ? { ...report, status: 'Resolved' }
        : report
    ));
    alert('Report resolved successfully!');
  };

  const handleDismissReport = (reportId: number) => {
    if (confirm('Are you sure you want to dismiss this report?')) {
      setReports(reports.filter(report => report.id !== reportId));
      alert('Report dismissed successfully!');
    }
  };

  const renderContentTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Content Management</h2>
        <button className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-add-line mr-2"></i>Add Content
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Learning Resources</h3>
            <i className="ri-book-line text-2xl text-teal-600"></i>
          </div>
          <p className="text-gray-600 mb-4">Manage tutorials, guides, and educational content</p>
          <div className="text-2xl font-bold text-teal-600 mb-2">{resources.length}</div>
          <p className="text-sm text-gray-500">Total resources</p>
          <button 
            onClick={handleManageResources}
            className="mt-4 w-full bg-teal-50 text-teal-600 py-2 rounded-lg hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Manage Resources
          </button>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Chat Messages</h3>
            <i className="ri-chat-3-line text-2xl text-blue-600"></i>
          </div>
          <p className="text-gray-600 mb-4">Monitor and moderate chat messages</p>
          <div className="text-2xl font-bold text-blue-600 mb-2">{messages.length}</div>
          <p className="text-sm text-gray-500">Total messages</p>
          <button 
            onClick={handleViewMessages}
            className="mt-4 w-full bg-blue-50 text-blue-600 py-2 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            View Messages
          </button>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Reported Content</h3>
            <i className="ri-flag-line text-2xl text-red-600"></i>
          </div>
          <p className="text-gray-600 mb-4">Review flagged content and reports</p>
          <div className="text-2xl font-bold text-red-600 mb-2">{reports.filter(r => r.status === 'Pending').length}</div>
          <p className="text-sm text-gray-500">Pending reviews</p>
          <button 
            onClick={handleReviewReports}
            className="mt-4 w-full bg-red-50 text-red-600 py-2 rounded-lg hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Review Reports
          </button>
        </div>
      </div>
    </div>
  );

  const renderStatsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Statistics & Analytics</h2>
        <button 
          onClick={fetchStatistics}
          disabled={statsLoading}
          className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
        >
          <i className={`${statsLoading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} mr-2`}></i>
          Refresh
        </button>
      </div>
      
      {statsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i>
            <p className="text-gray-600">Loading statistics...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalUsers.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-user-line text-2xl text-blue-600"></i>
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <span className={`font-medium ${stats.usersGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.usersGrowth >= 0 ? '+' : ''}{stats.usersGrowth}%
                </span>
                <span className="text-gray-600 ml-2">from last month</span>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Courts</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalCourts.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg">
                  <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <span className="text-gray-600">Active locations</span>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Sessions</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalSessions.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-lg">
                  <i className="ri-calendar-line text-2xl text-purple-600"></i>
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <span className={`font-medium ${stats.sessionsGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.sessionsGrowth >= 0 ? '+' : ''}{stats.sessionsGrowth}%
                </span>
                <span className="text-gray-600 ml-2">from last month</span>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Bookings</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalBookings.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg">
                  <i className="ri-bookmark-line text-2xl text-green-600"></i>
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <span className={`font-medium ${stats.bookingsGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.bookingsGrowth >= 0 ? '+' : ''}{stats.bookingsGrowth}%
                </span>
                <span className="text-gray-600 ml-2">from last month</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Courts</h3>
              <div className="space-y-4">
                {courts.slice(0, 5).map((court, index) => (
                  <div key={court.id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-sm font-medium text-teal-600 mr-3">
                        {index + 1}
                      </div>
                      <span className="text-gray-900">{court.name}</span>
                    </div>
                    <span className="text-gray-600 text-sm">{court.district}</span>
                  </div>
                ))}
                {courts.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No courts available</p>
                )}
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-user-add-line text-blue-600"></i>
                    </div>
                    <span className="text-gray-700">New Users (30 days)</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-900">
                    {Math.max(0, Math.round(stats.totalUsers * (stats.usersGrowth / 100)))}
                  </span>
                </div>
                
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-map-pin-add-line text-teal-600"></i>
                    </div>
                    <span className="text-gray-700">Active Courts</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-900">{stats.totalCourts}</span>
                </div>
                
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-calendar-check-line text-purple-600"></i>
                    </div>
                    <span className="text-gray-700">Active Sessions</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-900">{stats.totalSessions}</span>
                </div>
                
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-bookmark-3-line text-green-600"></i>
                    </div>
                    <span className="text-gray-700">Total Bookings</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-900">{stats.totalBookings}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'courts':
        return renderCourtsTab();
      case 'photos':
        return renderPhotosTab();
      case 'sessions':
        return renderSessionsTab();
      case 'users':
        return renderUsersTab();
      case 'content':
        return renderContentTab();
      case 'stats':
        return renderStatsTab();
      default:
        return renderStatsTab();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center">
          <i className="ri-check-line text-xl mr-2"></i>
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center max-w-md">
          <i className="ri-error-warning-line text-xl mr-2"></i>
          <div className="flex-1">{errorMessage}</div>
          <button 
            onClick={() => setErrorMessage('')}
            className="ml-2 text-white hover:text-gray-200"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      )}

      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Manage your pickleball community platform</p>
              <p className="text-sm text-gray-500 mt-1">Logged in as: {adminUser?.email}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-gray-600">
                <i className="ri-time-line mr-2"></i>
                Last updated: {new Date().toLocaleTimeString()}
              </div>
              <button 
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-box-line mr-2"></i>Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-teal-100 text-teal-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {renderTabContent()}
      </div>

      {/* User Detail Modal */}
      {showUserDetailModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">User Details</h2>
                <button
                  onClick={() => {
                    setShowUserDetailModal(false);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* User Avatar and Basic Info */}
                <div className="flex items-center space-x-4">
                  {selectedUser.avatar_url ? (
                    <img 
                      src={selectedUser.avatar_url} 
                      alt={selectedUser.nickname || 'User'} 
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center">
                      <i className="ri-user-line text-3xl text-teal-600"></i>
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {selectedUser.nickname || 'No nickname'}
                    </h3>
                    <p className="text-gray-600">{selectedUser.email}</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-2 ${
                      selectedUser.is_admin ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {selectedUser.is_admin ? 'Admin' : 'Member'}
                    </span>
                  </div>
                </div>

                {/* User Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedUser.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Join Date</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {new Date(selectedUser.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Skill Level</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {selectedUser.skill_level || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {selectedUser.location || 'Not specified'}
                    </p>
                  </div>
                </div>

                {/* Bio */}
                {selectedUser.bio && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                      {selectedUser.bio}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowUserDetailModal(false);
                      handleEditUser(selectedUser);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-2"></i>Edit User
                  </button>
                  <button
                    onClick={() => {
                      setShowUserDetailModal(false);
                      setSelectedUser(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit User</h2>
                <button
                  onClick={() => {
                    setShowEditUserModal(false);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleUpdateUser(); }} className="space-y-6">
                {/* User Avatar */}
                <div className="flex items-center space-x-4">
                  {selectedUser.avatar_url ? (
                    <img 
                      src={selectedUser.avatar_url} 
                      alt={selectedUser.nickname || 'User'} 
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                      <i className="ri-user-line text-2xl text-teal-600"></i>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">{selectedUser.email}</p>
                    <p className="text-xs text-gray-500">ID: {selectedUser.id.slice(0, 8)}...</p>
                  </div>
                </div>

                {/* Edit Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nickname
                    </label>
                    <input
                      type="text"
                      value={selectedUser.nickname || ''}
                      onChange={(e) => setSelectedUser({ ...selectedUser, nickname: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter nickname"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Skill Level
                    </label>
                    <select
                      value={selectedUser.skill_level || ''}
                      onChange={(e) => setSelectedUser({ ...selectedUser, skill_level: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select skill level</option>
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                      <option value="Professional">Professional</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={selectedUser.location || ''}
                      onChange={(e) => setSelectedUser({ ...selectedUser, location: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter location"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bio
                    </label>
                    <textarea
                      rows={4}
                      value={selectedUser.bio || ''}
                      onChange={(e) => setSelectedUser({ ...selectedUser, bio: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter bio"
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditUserModal(false);
                      setSelectedUser(null);
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-save-line mr-2"></i>Update User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Court Modal */}
      {showAddCourtModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Court</h2>
                <button
                  onClick={() => setShowAddCourtModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleAddCourt(); }} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Court Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={newCourt.name}
                      onChange={(e) => setNewCourt(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter court name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      required
                      value={newCourt.location}
                      onChange={(e) => setNewCourt(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter location"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      District *
                    </label>
                    <select
                      required
                      value={newCourt.district}
                      onChange={(e) => setNewCourt(prev => ({ ...prev, district: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-8"
                    >
                      <option value="">Select District</option>
                      <option value="Central">Central</option>
                      <option value="Wan Chai">Wan Chai</option>
                      <option value="Eastern">Eastern</option>
                      <option value="Southern">Southern</option>
                      <option value="Yau Tsim Mong">Yau Tsim Mong</option>
                      <option value="Sham Shui Po">Sham Shui Po</option>
                      <option value="Kowloon City">Kowloon City</option>
                      <option value="Wong Tai Sin">Wong Tai Sin</option>
                      <option value="Kwun Tong">Kwun Tong</option>
                      <option value="Tsuen Wan">Tsuen Wan</option>
                      <option value="Tuen Mun">Tuen Mun</option>
                      <option value="Yuen Long">Yuen Long</option>
                      <option value="North">North</option>
                      <option value="Tai Po">Tai Po</option>
                      <option value="Sha Tin">Sha Tin</option>
                      <option value="Sai Kung">Sai Kung</option>
                      <option value="Islands">Islands</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hourly Rate (HKD)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newCourt.hourly_rate}
                      onChange={(e) => setNewCourt(prev => ({ ...prev, hourly_rate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter hourly rate"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <input
                    type="text"
                    required
                    value={newCourt.address}
                    onChange={(e) => setNewCourt(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter full address"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={newCourt.contact_phone}
                      onChange={(e) => setNewCourt(prev => ({ ...prev, contact_phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter contact phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={newCourt.contact_email}
                      onChange={(e) => setNewCourt(prev => ({ ...prev, contact_email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter contact email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={newCourt.description}
                    onChange={(e) => setNewCourt(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter court description"
                  />
                </div>

                {/* Amenities */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Amenities
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {['Parking', 'Lighting', 'Changing Rooms', 'Showers', 'Equipment Rental', 'Pro Shop', 'Cafe', 'Air Conditioning', 'Lockers'].map((amenity) => (
                      <label key={amenity} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newCourt.amenities.includes(amenity)}
                          onChange={() => handleAmenityToggle(amenity)}
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                        <span className="text-sm text-gray-700">{amenity}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Operating Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Operating Hours
                  </label>
                  <div className="space-y-3">
                    {Object.entries(newCourt.operating_hours).map(([day, hours]) => (
                      <div key={day} className="flex items-center space-x-4">
                        <div className="w-20 text-sm font-medium text-gray-700 capitalize">
                          {day}
                        </div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={!hours.closed}
                            onChange={(e) => setNewCourt(prev => ({
                              ...prev,
                              operating_hours: {
                                ...prev.operating_hours,
                                [day]: { ...hours, closed: !e.target.checked }
                              }
                            }))}
                            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700">Open</span>
                        </label>
                        {!hours.closed && (
                          <>
                            <input
                              type="time"
                              value={hours.open}
                              onChange={(e) => setNewCourt(prev => ({
                                ...prev,
                                operating_hours: {
                                  ...prev.operating_hours,
                                  [day]: { ...hours, open: e.target.value }
                                }
                              }))}
                              className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            />
                            <span className="text-gray-500">to</span>
                            <input
                              type="time"
                              value={hours.close}
                              onChange={(e) => setNewCourt(prev => ({
                                ...prev,
                                operating_hours: {
                                  ...prev.operating_hours,
                                  [day]: { ...hours, close: e.target.value }
                                }
                              }))}
                              className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setShowAddCourtModal(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Add Court
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Court Modal */}
      {showEditModal && editingCourt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Court</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCourt(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleUpdateCourt(); }} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Court Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={editingCourt.name}
                      onChange={(e) => setEditingCourt(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter court name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      required
                      value={editingCourt.location}
                      onChange={(e) => setEditingCourt(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter location"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      District *
                    </label>
                    <select
                      required
                      value={editingCourt.district}
                      onChange={(e) => setEditingCourt(prev => ({ ...prev, district: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-8"
                    >
                      <option value="">Select District</option>
                      <option value="Central">Central</option>
                      <option value="Wan Chai">Wan Chai</option>
                      <option value="Eastern">Eastern</option>
                      <option value="Southern">Southern</option>
                      <option value="Yau Tsim Mong">Yau Tsim Mong</option>
                      <option value="Sham Shui Po">Sham Shui Po</option>
                      <option value="Kowloon City">Kowloon City</option>
                      <option value="Wong Tai Sin">Wong Tai Sin</option>
                      <option value="Kwun Tong">Kwun Tong</option>
                      <option value="Tsuen Wan">Tsuen Wan</option>
                      <option value="Tuen Mun">Tuen Mun</option>
                      <option value="Yuen Long">Yuen Long</option>
                      <option value="North">North</option>
                      <option value="Tai Po">Tai Po</option>
                      <option value="Sha Tin">Sha Tin</option>
                      <option value="Sai Kung">Sai Kung</option>
                      <option value="Islands">Islands</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hourly Rate (HKD)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editingCourt.hourly_rate || ''}
                      onChange={(e) => setEditingCourt(prev => ({ ...prev, hourly_rate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter hourly rate"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <input
                    type="text"
                    required
                    value={editingCourt.address}
                    onChange={(e) => setEditingCourt(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter full address"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={editingCourt.contact_phone || ''}
                      onChange={(e) => setEditingCourt(prev => ({ ...prev, contact_phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter contact phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={editingCourt.contact_email || ''}
                      onChange={(e) => setEditingCourt(prev => ({ ...prev, contact_email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter contact email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={editingCourt.description || ''}
                    onChange={(e) => setEditingCourt(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter court description"
                  />
                </div>

                {/* Amenities */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Amenities
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {['Parking', 'Lighting', 'Changing Rooms', 'Showers', 'Equipment Rental', 'Pro Shop', 'Cafe', 'Air Conditioning', 'Lockers'].map((amenity) => (
                      <label key={amenity} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingCourt.amenities.includes(amenity)}
                          onChange={() => handleEditAmenityToggle(amenity)}
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                        <span className="text-sm text-gray-700">{amenity}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Operating Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Operating Hours
                  </label>
                  <div className="space-y-3">
                    {Object.entries(editingCourt.operating_hours).map(([day, hours]) => (
                      <div key={day} className="flex items-center space-x-4">
                        <div className="w-20 text-sm font-medium text-gray-700 capitalize">
                          {day}
                        </div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={!hours.closed}
                            onChange={(e) => setEditingCourt(prev => ({
                              ...prev,
                              operating_hours: {
                                ...prev.operating_hours,
                                [day]: { ...hours, closed: !e.target.checked }
                              }
                            }))}
                            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700">Open</span>
                        </label>
                        {!hours.closed && (
                          <>
                            <input
                              type="time"
                              value={hours.open}
                              onChange={(e) => setEditingCourt(prev => ({
                                ...prev,
                                operating_hours: {
                                  ...prev.operating_hours,
                                  [day]: { ...hours, open: e.target.value }
                                }
                              }))}
                              className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            />
                            <span className="text-gray-500">to</span>
                            <input
                              type="time"
                              value={hours.close}
                              onChange={(e) => setEditingCourt(prev => ({
                                ...prev,
                                operating_hours: {
                                  ...prev.operating_hours,
                                  [day]: { ...hours, close: e.target.value }
                                }
                              }))}
                              className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingCourt(null);
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    Update Court
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Photo Upload Modal */}
      {showPhotoModal && selectedCourtForPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Upload Court Photo</h2>
                <button
                  onClick={() => {
                    setShowPhotoModal(false);
                    setSelectedCourtForPhoto(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="mb-4">
                <h3 className="font-semibold text-gray-900">{selectedCourtForPhoto.name}</h3>
                <p className="text-sm text-gray-600">{selectedCourtForPhoto.location}, {selectedCourtForPhoto.district}</p>
              </div>

              {selectedCourtForPhoto.photo_url && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Current Photo:</p>
                  <img
                    src={selectedCourtForPhoto.photo_url}
                    alt={selectedCourtForPhoto.name}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                </div>
              )}

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                const file = formData.get('photo') as File;
                if (file && file.size > 0) {
                  handlePhotoUpload(selectedCourtForPhoto.id, file);
                }
              }}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Photo
                  </label>
                  <input
                    type="file"
                    name="photo"
                    accept="image/*"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Supported formats: JPG, PNG, WebP. Max size: 5MB
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex">
                    <i className="ri-information-line text-yellow-600 mr-2 mt-0.5"></i>
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Photo Guidelines:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Use high-quality photos of the actual court</li>
                        <li>Ensure you have permission to use the image</li>
                        <li>Recommended size: 800x600 pixels or larger</li>
                        <li>Show the court clearly with good lighting</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPhotoModal(false);
                      setSelectedCourtForPhoto(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingPhoto}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center"
                  >
                    {uploadingPhoto ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-line mr-2"></i>
                        Upload Photo
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCourt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete Court</h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                className="text-gray-400 hover:text-gray-600 cursor-pointer disabled:opacity-50"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-delete-bin-line text-xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900">Delete "{selectedCourt.name}"?</h4>
                  <p className="text-sm text-gray-600">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <i className="ri-alert-line text-red-600 mr-2 mt-0.5"></i>
                  <div className="text-sm text-red-700">
                    <p className="font-medium mb-1">Warning: This will permanently delete:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>The court and all its details</li>
                      <li>All associated sessions</li>
                      <li>All session bookings</li>
                      <li>All uploaded photos</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteCourt}
                disabled={deleteLoading}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center"
              >
                {deleteLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line mr-2"></i>
                    Delete Court
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages Modal */}
      {showMessagesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Direct Messages</h2>
                  <p className="text-sm text-gray-600 mt-1">Monitor and manage user conversations</p>
                </div>
                <button
                  onClick={() => setShowMessagesModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {messagesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
                    <p className="text-gray-600">Loading messages...</p>
                  </div>
                </div>
              ) : messages.length > 0 ? (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div key={message.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="flex items-center space-x-2">
                            {message.sender?.avatar_url ? (
                              <img 
                                src={message.sender.avatar_url} 
                                alt={message.sender.nickname || 'Sender'} 
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                                <i className="ri-user-line text-teal-600"></i>
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">
                                {message.sender?.nickname || 'Unknown User'}
                              </p>
                              <p className="text-xs text-gray-500">{message.sender?.email}</p>
                            </div>
                          </div>
                          
                          <i className="ri-arrow-right-line text-gray-400"></i>
                          
                          <div className="flex items-center space-x-2">
                            {message.receiver?.avatar_url ? (
                              <img 
                                src={message.receiver.avatar_url} 
                                alt={message.receiver.nickname || 'Receiver'} 
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <i className="ri-user-line text-blue-600"></i>
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">
                                {message.receiver?.nickname || 'Unknown User'}
                              </p>
                              <p className="text-xs text-gray-500">{message.receiver?.email}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">
                            {new Date(message.created_at).toLocaleString()}
                          </span>
                          <button
                            onClick={() => handleDeleteMessage(message.id)}
                            className="text-red-600 hover:text-red-800 cursor-pointer p-2"
                            title="Delete message"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-lg p-3 ml-13">
                        <p className="text-gray-800">{message.content}</p>
                        {message.is_read && (
                          <div className="flex items-center mt-2 text-xs text-gray-500">
                            <i className="ri-check-double-line mr-1"></i>
                            Read
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <i className="ri-chat-3-line text-6xl text-gray-300 mb-4"></i>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No messages found</h3>
                  <p className="text-gray-600">No direct messages have been sent yet.</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {messages.length} messages
                </div>
                <button
                  onClick={fetchMessages}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line mr-2"></i>Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resources Management Modal */}
      {showResourcesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Learning Resources Management</h2>
                  <p className="text-sm text-gray-600 mt-1">Manage tutorials, guides, and educational content</p>
                </div>
                <button
                  onClick={() => setShowResourcesModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              
              {/* Language Filter */}
              <div className="flex space-x-2">
                <button
                  onClick={() => setResourceLanguageFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    resourceLanguageFilter === 'all'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Languages
                </button>
                <button
                  onClick={() => setResourceLanguageFilter('en')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    resourceLanguageFilter === 'en'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setResourceLanguageFilter('zh')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    resourceLanguageFilter === 'zh'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  中文
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {resourcesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4"></i>
                    <p className="text-gray-600">Loading resources...</p>
                  </div>
                </div>
              ) : resources.filter(resource => 
                resourceLanguageFilter === 'all' || resource.language === resourceLanguageFilter
              ).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {resources.filter(resource => 
                    resourceLanguageFilter === 'all' || resource.language === resourceLanguageFilter
                  ).map((resource) => (
                    <div key={resource.id} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      <div className="aspect-video bg-gray-200">
                        <img
                          src={resource.thumbnail_url}
                          alt={resource.title}
                          className="w-full h-full object-cover object-bottom"
                        />
                      </div>
                      
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-1">{resource.title}</h3>
                            {resource.language === 'zh' && resource.title !== resource.title_zh && (
                              <p className="text-xs text-gray-500">{resource.title_zh}</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-1 ml-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              resource.language === 'en' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {resource.language === 'en' ? 'EN' : '中文'}
                            </span>
                            {resource.is_featured && (
                              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full whitespace-nowrap">
                                Featured
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {resource.language === 'zh' ? resource.description_zh : resource.description}
                        </p>
                        
                        <div className="flex items-center justify-between mb-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            resource.skill_level === 'Beginner' ? 'bg-green-100 text-green-800' :
                            resource.skill_level === 'Intermediate' ? 'bg-blue-100 text-blue-800' :
                            resource.skill_level === 'Advanced' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {resource.skill_level}
                          </span>
                          <div className="flex items-center text-xs text-gray-500">
                            <i className="ri-eye-line mr-1"></i>
                            {resource.view_count.toLocaleString()}
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleToggleFeature(resource.id)}
                            className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                              resource.is_featured 
                                ? 'bg-yellow-600 text-white hover:bg-yellow-700' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {resource.is_featured ? 'Unfeature' : 'Feature'}
                          </button>
                          <button
                            onClick={() => handleDeleteResource(resource.id)}
                            className="px-3 py-2 bg-red-600 text-white rounded-md text-xs font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <i className="ri-book-line text-6xl text-gray-300 mb-4"></i>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No resources found</h3>
                  <p className="text-gray-600">
                    {resourceLanguageFilter === 'all' 
                      ? 'No learning resources have been created yet.'
                      : `No ${resourceLanguageFilter === 'en' ? 'English' : 'Chinese'} resources found.`
                    }
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {resources.filter(resource => 
                    resourceLanguageFilter === 'all' || resource.language === resourceLanguageFilter
                  ).length} resources
                  {resourceLanguageFilter !== 'all' && (
                    <span className="ml-1">
                      ({resourceLanguageFilter === 'en' ? 'English' : 'Chinese'})
                    </span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={fetchResources}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line mr-2"></i>Refresh
                  </button>
                  <button className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-add-line mr-2"></i>Add Resource
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reports Review Modal */}
      {showReportsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Content Reports Review</h2>
                  <p className="text-sm text-gray-600 mt-1">Review and moderate reported content</p>
                </div>
                <button
                  onClick={() => setShowReportsModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {reportsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <i className="ri-loader-4-line text-4xl text-red-600 animate-spin mb-4"></i>
                    <p className="text-gray-600">Loading reports...</p>
                  </div>
                </div>
              ) : reports.length > 0 ? (
                <div className="space-y-4">
                  {reports.map((report) => (
                    <div key={report.id} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                              report.report_type === 'Inappropriate Content' ? 'bg-red-100 text-red-800' :
                              report.report_type === 'Spam' ? 'bg-orange-100 text-orange-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {report.report_type}
                            </span>
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                              report.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {report.status}
                            </span>
                          </div>
                          
                          <h3 className="font-semibold text-gray-900 mb-2">Reported Item: {report.reported_item}</h3>
                          <p className="text-gray-700 mb-3">{report.reason}</p>
                          
                          <div className="flex items-center text-sm text-gray-600">
                            <div className="flex items-center mr-6">
                              <i className="ri-user-line mr-2"></i>
                              <span>Reported by: {report.reporter_name}</span>
                            </div>
                            <div className="flex items-center">
                              <i className="ri-time-line mr-2"></i>
                              <span>{new Date(report.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        {report.status === 'Pending' && (
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => handleResolveReport(report.id)}
                              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-check-line mr-2"></i>Resolve
                            </button>
                            <button
                              onClick={() => handleDismissReport(report.id)}
                              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-close-line mr-2"></i>Dismiss
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <i className="ri-flag-line text-6xl text-gray-300 mb-4"></i>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No reports found</h3>
                  <p className="text-gray-600">No content has been reported yet.</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {reports.length} reports ({reports.filter(r => r.status === 'Pending').length} pending)
                </div>
                <button
                  onClick={fetchReports}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line mr-2"></i>Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PhotoManagement = () => {
  const [courts, setCourts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<string | null>(null);
  const { adminUser } = useAdminAuth();

  useEffect(() => {
    fetchCourts();
  }, []);

  const fetchCourts = async () => {
    try {
      const { data, error } = await supabase
        .from('courts')
        .select('*')
        .order('name');

      if (error) throw error;
      setCourts(data || []);
    } catch (error) {
      console.error('Error fetching courts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (courtId: string, file: File) => {
    if (!adminUser) return;

    setUploading(courtId);
    try {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Invalid file type. Only JPG, PNG, and WebP are allowed.');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size too large. Maximum 5MB allowed.');
        return;
      }

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `court-${courtId}-${Date.now()}.${fileExt}`;
      
      // Upload to Supabase Storage using client
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('court-photos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error('Failed to upload file: ' + uploadError.message);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('court-photos')
        .getPublicUrl(fileName);

      // Update court record with photo URL
      const { error: updateError } = await supabase
        .from('courts')
        .update({ photo_url: publicUrl })
        .eq('id', courtId);

      if (updateError) {
        console.error('Update error:', updateError);
        throw new Error('Failed to update court photo: ' + updateError.message);
      }

      // Update local state
      setCourts(courts.map(court => 
        court.id === courtId 
          ? { ...court, photo_url: publicUrl }
          : court
      ));

      alert('Photo uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploading(null);
    }
  };

  const handlePhotoDelete = async (courtId: string) => {
    if (!adminUser || !confirm('Are you sure you want to delete this photo?')) return;

    setUploading(courtId);
    try {
      // Find the court to get the photo URL
      const court = courts.find(c => c.id === courtId);
      if (!court?.photo_url) return;

      // Extract filename from URL
      const urlParts = court.photo_url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      
      // Delete from storage using client
      const { error: deleteError } = await supabase.storage
        .from('court-photos')
        .remove([fileName]);

      if (deleteError) {
        console.error('Delete error:', deleteError);
        throw new Error('Failed to delete file: ' + deleteError.message);
      }

      // Update court record to remove photo URL
      const { error: updateError } = await supabase
        .from('courts')
        .update({ photo_url: null })
        .eq('id', courtId);

      if (updateError) {
        console.error('Update error:', updateError);
        throw new Error('Failed to update court record: ' + updateError.message);
      }

      // Update local state
      setCourts(courts.map(court => 
        court.id === courtId 
          ? { ...court, photo_url: null }
          : court
      ));

      alert('Photo deleted successfully!');
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Delete failed: ${error.message}`);
    } finally {
      setUploading(null);
    }
  };

  const handleDeletePhoto = async (courtId: string, photoUrl: string) => {
    await handlePhotoDelete(courtId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Photo Upload Guidelines</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Use high-quality photos of actual court facilities</li>
          <li>• Recommended size: 800x600 pixels or larger</li>
          <li>• Supported formats: JPG, PNG, WebP</li>
          <li>• Maximum file size: 5MB</li>
          <li>• Photos should show the court clearly and professionally</li>
        </ul>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courts.map((court) => (
          <div key={court.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="aspect-video bg-gray-100 relative">
              {court.photo_url ? (
                <img
                  src={court.photo_url}
                  alt={court.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <i className="ri-image-line text-4xl"></i>
                </div>
              )}
              {uploading === court.id && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              )}
            </div>
            
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-1">{court.name}</h3>
              <p className="text-sm text-gray-600">{court.location}</p>
              
              <div className="flex gap-2">
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handlePhotoUpload(court.id, file);
                      }
                    }}
                    className="hidden"
                    disabled={uploading === court.id}
                  />
                  <div className="w-full bg-teal-600 text-white px-3 py-2 rounded-md text-sm font-medium text-center cursor-pointer hover:bg-teal-700 transition-colors whitespace-nowrap">
                    {court.photo_url ? 'Replace' : 'Upload'}
                  </div>
                </label>
                
                {court.photo_url && (
                  <button
                    onClick={() => handlePhotoDelete(court.id)}
                    disabled={uploading === court.id}
                    className="px-3 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Admin;
